/**
 * Error Tracking Service
 *
 * Centralized error reporting and tracking.
 */

/**
 * Error severity levels
 */
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * Error context information
 */
export interface ErrorContext {
  componentStack?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  extra?: Record<string, unknown>;
}

/**
 * Error report structure
 */
export interface ErrorReport {
  message: string;
  stack?: string;
  severity: ErrorSeverity;
  context: ErrorContext;
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get or create session ID from storage
 */
function getSessionId(): string {
  const key = 'agentmesh_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

/**
 * Error queue for batching
 */
const errorQueue: ErrorReport[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Flush error queue to backend
 */
async function flushErrorQueue(): Promise<void> {
  if (errorQueue.length === 0) return;

  const errors = [...errorQueue];
  errorQueue.length = 0;

  try {
    // In production, this would send to an error tracking service
    // For now, we log to console and could send to our backend
    if (import.meta.env.DEV) {
      console.group('Error Reports');
      errors.forEach((error) => {
        console.error(`[${error.severity.toUpperCase()}]`, error.message, error);
      });
      console.groupEnd();
    }

    // TODO: Send to backend error tracking endpoint
    // await api.post('/errors/report', { errors });
  } catch (e) {
    // Re-add errors to queue if sending failed
    errorQueue.push(...errors);
    console.error('Failed to send error reports:', e);
  }
}

/**
 * Schedule queue flush
 */
function scheduleFlush(): void {
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushErrorQueue();
  }, 5000); // Flush every 5 seconds
}

/**
 * Determine error severity based on error type
 */
function determineSeverity(error: Error): ErrorSeverity {
  const message = error.message.toLowerCase();

  // Critical errors
  if (
    message.includes('chunk') ||
    message.includes('module') ||
    message.includes('network')
  ) {
    return 'critical';
  }

  // High severity
  if (
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('authentication')
  ) {
    return 'high';
  }

  // Medium severity
  if (
    message.includes('validation') ||
    message.includes('not found') ||
    message.includes('timeout')
  ) {
    return 'medium';
  }

  return 'low';
}

/**
 * Track an error
 */
export function trackError(
  error: Error,
  context: Partial<ErrorContext> = {},
  severity?: ErrorSeverity
): void {
  const errorReport: ErrorReport = {
    message: error.message,
    stack: error.stack,
    severity: severity || determineSeverity(error),
    context: {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      sessionId: getSessionId(),
      ...context,
    },
  };

  errorQueue.push(errorReport);
  scheduleFlush();

  // Immediately flush critical errors
  if (errorReport.severity === 'critical') {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
      flushTimeout = null;
    }
    flushErrorQueue();
  }
}

/**
 * Track a React error boundary error
 */
export function trackBoundaryError(
  error: Error,
  componentStack: string
): void {
  trackError(
    error,
    { componentStack },
    'critical'
  );
}

/**
 * Track an unhandled promise rejection
 */
export function trackUnhandledRejection(
  reason: unknown
): void {
  const error =
    reason instanceof Error
      ? reason
      : new Error(String(reason));

  trackError(error, { extra: { type: 'unhandled_rejection' } }, 'high');
}

/**
 * Track a console error
 */
export function trackConsoleError(
  ...args: unknown[]
): void {
  const message = args
    .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
    .join(' ');

  const error = new Error(message);
  trackError(error, { extra: { type: 'console_error' } }, 'low');
}

/**
 * Initialize global error handlers
 */
export function initializeErrorTracking(): void {
  // Track unhandled errors
  window.addEventListener('error', (event) => {
    trackError(
      event.error || new Error(event.message),
      {
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      }
    );
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    trackUnhandledRejection(event.reason);
  });

  // Override console.error in production to track errors
  if (import.meta.env.PROD) {
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      trackConsoleError(...args);
      originalConsoleError.apply(console, args);
    };
  }

  // Flush errors before page unload
  window.addEventListener('beforeunload', () => {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
    }
    flushErrorQueue();
  });
}

/**
 * Get error statistics (for debugging)
 */
export function getErrorStats(): {
  queuedErrors: number;
  sessionId: string;
} {
  return {
    queuedErrors: errorQueue.length,
    sessionId: getSessionId(),
  };
}

export default {
  trackError,
  trackBoundaryError,
  trackUnhandledRejection,
  initializeErrorTracking,
  getErrorStats,
};
